import { useEffect } from 'react';
import { useUser } from '@/lib/auth/react';
import { useIntercomGetIdentity } from '@/lib/endpoints';
import { isIntercomEnabled } from './config';
import { intercomController } from './intercom-controller';
import { reconcileIntercom } from './identity';

/**
 * Mounts the Intercom messenger and keeps its session in sync with auth.
 * Side-effect only (renders nothing) — sits beside the other realtime/identity
 * bridges in {@link App}. The messenger boots for everyone (anonymous visitors
 * included) with the default launcher bubble hidden — it opens only via the
 * explicit Support entry points — and re-identifies once the player logs in and
 * the server identity (`user_id` + Identity-Verification `user_hash`) resolves.
 *
 * Disabled (no boot, no network) when no app id is configured or under E2E —
 * see {@link isIntercomEnabled}.
 */
export function IntercomMessenger(): null {
  const enabled = isIntercomEnabled();
  const user = useUser();

  // Scope the identity cache by user so a previous player's hash is never
  // served after a logout→login switch (the generated query key is static).
  // staleTime:0 forces a fresh fetch each time a player becomes present, so a
  // rapid re-login can't boot the messenger with a stale (e.g. rotated) hash.
  const userKey = user ? String(user.userId) : 'anon';
  const { data: identity } = useIntercomGetIdentity({
    query: {
      enabled: enabled && Boolean(user),
      queryKey: ['/intercom/identity', userKey],
      staleTime: 0,
    },
  });

  // No cleanup on purpose: this component is mounted for the app's lifetime and
  // reconcileIntercom owns shutdown (on logout/user-switch). A `return () =>
  // shutdown()` here would fire on every user/identity change — churning the
  // messenger — not only on unmount.
  useEffect(() => {
    if (!enabled) return;
    reconcileIntercom(intercomController, { user: user ?? null, identity: identity ?? null });
  }, [enabled, user, identity]);

  return null;
}
